import type { ReactNode } from "react";

const B = ({ children }: { children: ReactNode }) => (
  <strong className="font-medium text-[var(--color-fg)]">{children}</strong>
);

const realData: ReactNode[] = [
  <>Average immigration firm: 1-2 advisors, 5 cases/month per advisor</>,
  <>70% of advisor time is non-value-add, limiting output</>,
  <>Xeni reduces form processing from hours to minutes</>,
  <>Typical firm gross margin: <B>~25%</B></>,
];

const seedProves: ReactNode[] = [
  <>Acquire 1-2 firms, deploy COSX + Xeni into live operations</>,
  <>Establish benchmark framework across key workflows</>,
  <>Demonstrate agent accuracy improvement via harness engineering</>,
  <><B>2X advisor capacity</B> per head</>,
  <>Generate before/after P&L transformation data</>,
  <>Target: <B>gross margin from 25% to ~50%</B></>,
];

const timeline = [
  { stage: "Angel (Done)", detail: "Pre-revenue · paid contracts signed · Dec 2025", valuation: "£5M", active: false },
  { stage: "Seed (Now)", detail: (<>1-2 firms · 2-3 advisors · <B>3 countries</B> · Q2 2026</>), valuation: "£7.5M", active: true },
  { stage: "Series A", detail: (<>10+ firms · ~15 advisors · 12-15x multiple · Q1 2027</>), valuation: "£30-50M", active: false },
];

export function V2RoadmapSlide({ isActive: _ }: { isActive: boolean }) {
  return (
    <div className="flex flex-col h-full">
      <h2 className="text-[32px] font-light tracking-tight mb-5 font-[family-name:var(--font-heading)]">
        From seed to scale
      </h2>

      <div className="flex flex-row gap-6">
        {/* Left: What we're proving */}
        <div className="flex-1">
          <div className="text-[12px] font-[family-name:var(--font-mono)] font-bold text-[var(--color-accent)] uppercase tracking-[0.1em] mb-4 pb-2 border-b border-[var(--color-border)]">
            What we&apos;re proving with seed
          </div>
          <div className="mb-4">
            <div className="text-[11px] font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)] uppercase tracking-wider mb-2">
              What we know (real data):
            </div>
            <ul className="space-y-1">
              {realData.map((d, i) => (
                <li key={i} className="flex items-start gap-2 text-[12px] text-[var(--color-fg-secondary)]">
                  <span className="w-1 h-1 rounded-full bg-[var(--color-fg-tertiary)] mt-[6px] shrink-0" />
                  {d}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-[11px] font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)] uppercase tracking-wider mb-2">
              What seed capital proves:
            </div>
            <ul className="space-y-1">
              {seedProves.map((d, i) => (
                <li key={i} className="flex items-start gap-2 text-[12px] text-[var(--color-fg-secondary)]">
                  <span className="w-1 h-1 rounded-full bg-[var(--color-accent)] mt-[6px] shrink-0" />
                  {d}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Right: Investor growth path + ARR ramp */}
        <div className="flex-1">
          <div className="text-[12px] font-[family-name:var(--font-mono)] font-bold text-[var(--color-accent)] uppercase tracking-[0.1em] mb-4 pb-2 border-b border-[var(--color-border)]">
            Investor growth path
          </div>

          {/* Combined timeline + ARR bars */}
          <div className="flex flex-col gap-3">
            {timeline.map((t, i) => {
              const barWidths = [10, 50, 260];
              const barColors = [
                "var(--color-fg-tertiary)",
                "var(--color-accent)",
                "var(--color-accent)",
              ];
              const barLabels = ["Pre-revenue → £60K projected ARR", "£60K → £300K ARR", "£2-3M ARR"];
              return (
                <div key={t.stage}>
                  <div className="flex items-baseline gap-2 mb-0.5">
                    <div className={`w-2 h-2 rounded-full shrink-0 ${t.active ? "bg-[var(--color-accent)]" : "bg-[var(--color-fg-tertiary)]"}`} />
                    <span className="text-[12px] font-medium">{t.stage}</span>
                    <span className="text-[16px] font-light tracking-tight font-[family-name:var(--font-heading)] ml-auto">{t.valuation}</span>
                  </div>
                  <div className="text-[10px] text-[var(--color-fg-tertiary)] font-[family-name:var(--font-mono)] ml-4 mb-1.5">{t.detail}</div>
                  {/* ARR bar */}
                  <div className="ml-4 flex items-center gap-2">
                    <div
                      className="h-[14px] rounded-sm"
                      style={{
                        width: barWidths[i],
                        background: barColors[i],
                        opacity: 0.2,
                        border: `1px solid ${barColors[i]}`,
                      }}
                    />
                    <span className="text-[10px] font-medium font-[family-name:var(--font-mono)] text-[var(--color-fg-secondary)]">
                      {barLabels[i]}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Firm count scale */}
          <div className="mt-3 ml-4 flex items-center gap-1 text-[9px] font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)]">
            <span>1-2 firms</span>
            <span className="flex-1 border-t border-dashed border-[var(--color-border)]" />
            <span>10 firms</span>
            <span className="flex-1 border-t border-dashed border-[var(--color-border)]" />
            <span>20+ firms</span>
          </div>
          <p className="mt-2 ml-4 text-[9px] text-[var(--color-fg-tertiary)] font-[family-name:var(--font-mono)]">
            10 firms ≈ 15 advisors. At target capacity: <strong className="font-medium text-[var(--color-fg)]">£2.65M annual profit.</strong>
          </p>
        </div>
      </div>

      <div className="text-left mt-auto mb-8 pb-4 border-b border-[var(--color-border)]">
        <p className="text-[12px] text-[var(--color-fg-secondary)] font-light">
          Seed to Series A: <strong className="font-medium text-[var(--color-fg)]">4-7x valuation uplift in 12 months</strong>
        </p>
      </div>
    </div>
  );
}
